import { useState, useCallback, useEffect, useRef } from 'react';
import { get } from 'lodash';
import moment from 'moment-timezone';

import config from '../../../config';
import routeConfiguration from '../../../routeConfiguration';
import { pathByRouteName, findRouteByRouteName } from '../../../util/routes';
import * as log from '../../../util/log';
import {
  ensureCurrentUser,
  ensureTransaction,
  ensureStripeCustomer,
  ensurePaymentMethodCard,
} from '../../../util/data';
import { daysAdded, minutesBetween } from '../../../util/dates';
import { txIsPaymentPending, txIsPaymentExpired } from '../../../util/transaction';
import { isVerified } from '../../../util/user';
import { getGuestLiabilityProtectionValue } from '../../../util/guestLiabilityProtection';
import { ensureMembershipForLtPlusCheckout } from '../../../util/ltPlusSubscription';
import { getLodgingPricesOptions } from '../../../util/listing';
import { LODGING_TYPE, getPackageLodgingPrices } from '../../../util/package';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { storeData, storedData, clearData } from '../CheckoutPageSessionHelpers';
import { buildCustomPricingParams } from '../checkoutPricing';
import {
  buildLtPlusCompletionPageData,
  buildLtPlusCompletionSpeculateParams,
  getLtPlusSpeculationFallback,
  resolveCheckoutTransaction,
  resolveLtPlusSubscriptionId,
  shouldSkipGlpFieldChangeSpeculation,
  shouldSkipInsuranceSpeculationDuringLtPlus,
  shouldSkipLtPlusSelectionSpeculation,
  shouldSkipSpeculationDuringLtPlus,
} from '../checkoutLtPlusFlow';
import { useCheckoutLtPlusFlow } from './useCheckoutLtPlusFlow';

const { Money } = sdkTypes;

const STORAGE_KEY = 'CheckoutPage';

const ONETIME_PAYMENT = 'ONETIME_PAYMENT';
const PAY_AND_SAVE_FOR_LATER_USE = 'PAY_AND_SAVE_FOR_LATER_USE';
const USE_SAVED_CARD = 'USE_SAVED_CARD';

const paymentFlow = (selectedPaymentMethod, saveAfterOnetimePayment) => {
  return selectedPaymentMethod === 'defaultCard'
    ? USE_SAVED_CARD
    : saveAfterOnetimePayment
    ? PAY_AND_SAVE_FOR_LATER_USE
    : ONETIME_PAYMENT;
};

const initializeOrderPage = (initialValues, routes, dispatch) => {
  const OrderPage = findRouteByRouteName('OrderDetailsPage', routes);
  dispatch(OrderPage.setInitialValues(initialValues));
};

const checkIsPaymentExpired = existingTransaction => {
  return txIsPaymentExpired(existingTransaction)
    ? true
    : txIsPaymentPending(existingTransaction)
    ? minutesBetween(existingTransaction.attributes.lastTransitionedAt, new Date()) >= 15
    : false;
};

const getTripInsuranceSelection = transaction => {
  const tripInsurance = get(transaction, 'attributes.metadata.tripInsurance');
  return typeof tripInsurance === 'boolean' ? tripInsurance : undefined;
};

const getCheckoutSelectionsForStorage = pageData => {
  const selections = {};

  if (pageData?.guestLiabilityProtection !== undefined) {
    selections.guestLiabilityProtection = pageData.guestLiabilityProtection;
  }

  if (typeof pageData?.tripInsurance === 'boolean') {
    selections.tripInsurance = pageData.tripInsurance;
  }

  return selections;
};

const persistCheckoutPageData = pageData => {
  const { bookingData, bookingDates, listing, transaction, includeLodging } = pageData;

  if (bookingData && bookingDates && listing) {
    storeData(
      bookingData,
      bookingDates,
      listing,
      transaction,
      STORAGE_KEY,
      includeLodging,
      getCheckoutSelectionsForStorage(pageData)
    );
  }
};

export const useCheckoutPage = props => {
  const {
    bookingData,
    bookingDates,
    listing,
    transaction,
    includeLodging,
    fetchStripeCustomer,
    history,
    currentUser,
    speculatedTransaction,
    fetchSpeculatedTransaction,
    params,
    intl,
    stripeCustomerFetched,
    onInitiateOrder,
    onConfirmPayment,
    onSendMessage,
    onSavePaymentMethod,
    paymentIntent,
    onRetrievePaymentIntent,
    fetchUser,
    dispatch,
  } = props;

  const [pageData, setPageData] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [usedPromoCode, setUsedPromoCode] = useState(false);

  const stripeRef = useRef(null);
  const previousCurrentUserRef = useRef(null);

  const {
    isCompletingLtPlusSubscriptionRef,
    ltPlusSubscriptionIdRef,
    postLtPlusSpeculatedTransactionRef,
    handleBeginLtPlusSignup,
    handleEndLtPlusSignup,
  } = useCheckoutLtPlusFlow();

  const customPricingParams = useCallback(
    pricingParams => buildCustomPricingParams(pricingParams, { currentUser }),
    [currentUser]
  );

  const getSpeculateTransaction = useCallback(
    async (speculatePageData, isUpdating) => {
      const resolvedLtPlusSubscriptionId = resolveLtPlusSubscriptionId(
        speculatePageData.ltPlusSubscriptionId,
        ltPlusSubscriptionIdRef.current
      );

      if (
        shouldSkipSpeculationDuringLtPlus(
          isCompletingLtPlusSubscriptionRef.current,
          resolvedLtPlusSubscriptionId
        )
      ) {
        return getLtPlusSpeculationFallback({
          postLtPlusSpeculatedTransaction: postLtPlusSpeculatedTransactionRef.current,
          speculatedTransaction,
        });
      }

      const { bookingStart, bookingEnd, startLodgingDate } = speculatePageData.bookingDates;

      const bookingStartForAPI = bookingStart;
      const bookingEndForAPI = bookingEnd;
      const startLodgingDateForAPI = startLodgingDate;
      let includePromoCode = false;
      let promoCodeUsed = false;
      if (speculatePageData.promoCode) {
        const promoCodes = get(currentUser, 'attributes.profile.protectedData.promoCodes', []);
        const lowerCasePromoCode = speculatePageData.promoCode.toLowerCase();

        includePromoCode = !promoCodes.some(code => code.toLowerCase() === lowerCasePromoCode);
        promoCodeUsed = !includePromoCode;
      }
      setUsedPromoCode(promoCodeUsed);

      const packages = get(speculatePageData.listing, 'attributes.publicData.packages', []);
      const guestSize = get(speculatePageData, 'bookingData.party', 1);
      const flexibleGuestSize = get(speculatePageData, 'bookingData.flexibleGuestSize', null);
      const packageInfo = packages.find(item => item.id === params.packageId);
      let days = daysAdded(bookingStart, bookingEnd);
      if (startLodgingDate && startLodgingDate.getTime() === bookingStart.getTime()) {
        days -= 1;
      }

      const lodgingPricesOptions = getLodgingPricesOptions(speculatePageData.listing, intl);

      const { optionalLodging, includedLodging } = getPackageLodgingPrices(
        packageInfo,
        lodgingPricesOptions
      );

      const lodgingAddons = get(speculatePageData, 'bookingData.lodgingAddons', []);
      let lodgingPrices = (lodgingAddons || []).map(addon => {
        const lodgingPrice = JSON.parse(optionalLodging[addon].price, sdkTypes.reviver);

        return {
          price: lodgingPrice,
          type: optionalLodging[addon].type,
        };
      });

      if (includedLodging.length) {
        lodgingPrices = lodgingPrices.concat(
          includedLodging.map(item => {
            const lodgingPrice = JSON.parse(item.price, sdkTypes.reviver);

            return {
              price: lodgingPrice,
              type: item.type,
            };
          })
        );
      }

      const lodgingPrice = lodgingPrices.reduce((acc, curr) => {
        if (curr?.price?.amount) {
          return acc + curr.price.amount;
        }

        return acc;
      }, 0);
      const lodgingPriceForTax = lodgingPrices
        .filter(lodgingPriceItem =>
          [LODGING_TYPE.HOUSE, LODGING_TYPE.CABIN].includes(lodgingPriceItem.type)
        )
        .reduce((acc, curr) => {
          if (curr?.price?.amount) {
            return acc + curr.price.amount;
          }

          return acc;
        }, 0);
      const lodgingFee = lodgingPrice && new Money(lodgingPrice, config.currency);
      const lodgingFeeForTax = lodgingPriceForTax && new Money(lodgingPriceForTax, config.currency);

      let packageItem = null;

      if (packageInfo) {
        const unitPrice = JSON.parse(packageInfo.price);
        const totalPrice = new Money(unitPrice.amount * guestSize * days, unitPrice.currency);

        packageItem = {
          code: `line-item/${packageInfo.title.replace(/\s/g, '-')}`,
          title: packageInfo.title,
          description: packageInfo.description,
          unitPrice,
          totalHuntPrice: totalPrice,
          activityDays: days,
          quantity: guestSize * days,
          guestSize,
          activity: packageInfo.activity,
          hasLodging: speculatePageData?.includeLodging || packageInfo?.hasLodging,
          id: packageInfo.id,
          lodgingFee,
          lodgingFeeForTax,
          flexibleGuestSize,
        };
      }

      const stripePaymentIntents = get(
        speculatedTransaction,
        'attributes.protectedData.stripePaymentIntents',
        null
      );

      return fetchSpeculatedTransaction(
        customPricingParams({
          listing: speculatePageData.listing,
          bookingStart: bookingStartForAPI,
          bookingEnd: bookingEndForAPI,
          startLodgingDate: startLodgingDateForAPI,
          bookingFees: speculatePageData.bookingData,
          packageItem,
          promoCode: includePromoCode ? speculatePageData.promoCode : '',
          tripInsurance: speculatePageData.tripInsurance,
          guestLiabilityProtection: speculatePageData.guestLiabilityProtection,
          stripePaymentIntents,
          currentUser: speculatePageData.currentUser,
          ltPlusSubscriptionId: resolvedLtPlusSubscriptionId,
        }),
        isUpdating
      );
    },
    [
      currentUser,
      customPricingParams,
      fetchSpeculatedTransaction,
      intl,
      params.packageId,
      speculatedTransaction,
      isCompletingLtPlusSubscriptionRef,
      ltPlusSubscriptionIdRef,
      postLtPlusSpeculatedTransactionRef,
    ]
  );

  const loadInitialData = useCallback(() => {
    fetchStripeCustomer();

    const hasNavigatedThroughLink = history.action === 'PUSH' || history.action === 'REPLACE';
    const hasDataInProps = !!(bookingData && bookingDates && listing) && hasNavigatedThroughLink;
    if (hasDataInProps) {
      storeData(bookingData, bookingDates, listing, transaction, STORAGE_KEY, includeLodging);
    }

    let initialPageData = hasDataInProps
      ? { bookingData, bookingDates, listing, transaction, includeLodging }
      : storedData(STORAGE_KEY);

    const guestLiabilityProtection = getGuestLiabilityProtectionValue(
      currentUser,
      initialPageData?.guestLiabilityProtection
    );

    if (guestLiabilityProtection !== undefined) {
      initialPageData = { ...initialPageData, guestLiabilityProtection };

      if (initialPageData.bookingData && initialPageData.bookingDates && initialPageData.listing) {
        persistCheckoutPageData(initialPageData);
      }
    }

    const tx = initialPageData ? initialPageData.transaction : null;
    const isBookingCreated = tx && tx.booking && tx.booking.id;

    const shouldFetchSpeculatedTransaction =
      initialPageData &&
      initialPageData.listing &&
      initialPageData.listing.id &&
      initialPageData.bookingData &&
      initialPageData.bookingDates &&
      initialPageData.bookingDates.bookingStart &&
      initialPageData.bookingDates.bookingEnd &&
      !isBookingCreated;

    if (
      shouldFetchSpeculatedTransaction &&
      !shouldSkipInsuranceSpeculationDuringLtPlus(isCompletingLtPlusSubscriptionRef.current)
    ) {
      getSpeculateTransaction(initialPageData, false);
    }

    setPageData(initialPageData || {});
    setDataLoaded(true);
  }, [
    bookingData,
    bookingDates,
    listing,
    transaction,
    includeLodging,
    fetchStripeCustomer,
    history.action,
    currentUser,
    getSpeculateTransaction,
    isCompletingLtPlusSubscriptionRef,
  ]);

  useEffect(() => {
    if (currentUser && !previousCurrentUserRef.current) {
      loadInitialData();
    }
    previousCurrentUserRef.current = currentUser;
  }, [currentUser, loadInitialData]);

  const handlePaymentIntent = useCallback(
    async handlePaymentParams => {
      const {
        pageData: paymentPageData,
        speculatedTransaction: paymentSpeculatedTransaction,
        message,
        paymentIntent: orderPaymentIntent,
        selectedPaymentMethod,
        saveAfterOnetimePayment,
        currentUser: paymentCurrentUser,
      } = handlePaymentParams;

      const storedTx = ensureTransaction(paymentPageData.transaction);
      const tx = paymentSpeculatedTransaction || storedTx;

      const ensuredCurrentUser = ensureCurrentUser(paymentCurrentUser || currentUser);
      const ensuredStripeCustomer = ensureStripeCustomer(ensuredCurrentUser.stripeCustomer);
      const ensuredDefaultPaymentMethod = ensurePaymentMethodCard(
        ensuredStripeCustomer.defaultPaymentMethod
      );
      const currentTransaction = get(history, 'location.state.currentTransaction', null);

      const hasDefaultPaymentMethod = !!(
        stripeCustomerFetched &&
        ensuredStripeCustomer.attributes.stripeCustomerId &&
        ensuredDefaultPaymentMethod.id
      );
      const stripePaymentMethodId = hasDefaultPaymentMethod
        ? ensuredDefaultPaymentMethod.attributes.stripePaymentMethodId
        : null;

      const selectedPaymentFlow = paymentFlow(selectedPaymentMethod, saveAfterOnetimePayment);
      const { listing: paymentListing } = paymentPageData;
      const landownerId = get(paymentListing, 'author.id.uuid', null);

      const fnRequestPayment = async fnParams => {
        return onInitiateOrder(
          fnParams,
          currentTransaction?.id ? currentTransaction.id : storedTx.id,
          currentTransaction
        );
      };

      const optionalPaymentParams =
        selectedPaymentFlow === USE_SAVED_CARD && hasDefaultPaymentMethod
          ? { paymentMethod: stripePaymentMethodId }
          : selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE
          ? { setupPaymentMethodForSaving: true }
          : {};
      const bookingFeeLineItem = tx.attributes.lineItems;
      const filteredLineItems = bookingFeeLineItem.filter(lineItem => {
        return (
          lineItem.code !== 'line-item/customer-commission' &&
          lineItem.code !== 'line-item/provider-commission'
        );
      });

      const lineItems = filteredLineItems || null;

      const orderParams = {
        listingId: paymentPageData.listing.id,
        bookingStart: tx.booking.attributes.start,
        bookingEnd: tx.booking.attributes.end,
        bookingDisplayStart: tx.booking.attributes.displayStart,
        bookingDisplayEnd: tx.booking.attributes.displayEnd,
        landownerId,
        ...optionalPaymentParams,
        lineItems,
        protectedData: {
          ...tx.attributes.protectedData,
        },
        metadata: {
          ...tx.attributes.metadata,
        },
        currentUser: {
          id: ensuredCurrentUser.id,
        },
        ltPlusSubscriptionId: resolveLtPlusSubscriptionId(
          undefined,
          ltPlusSubscriptionIdRef.current
        ),
      };

      // Keeps the id once the order exists, so a later failure does not
      // let the guest submit (and potentially pay for) a second order.
      let createdOrderId = null;

      try {
        const paymentResponse = await fnRequestPayment(orderParams);
        const order = ensureTransaction(paymentResponse);

        if (order.id) {
          createdOrderId = order.id;
          const {
            bookingData: orderBookingData,
            bookingDates: orderBookingDates,
          } = paymentPageData;
          storeData(orderBookingData, orderBookingDates, paymentListing, order, STORAGE_KEY);
          setPageData({ ...paymentPageData, transaction: order });
        }

        const confirmPaymentResponse = await onConfirmPayment({
          transactionId: order.id,
        });

        const sendMessageResponse = await onSendMessage({
          ...confirmPaymentResponse,
          txId: order.id,
          message,
          customerId: ensuredCurrentUser.id.uuid,
          providerId: landownerId,
          senderType: 'customer',
        });

        if (selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE) {
          return onSavePaymentMethod(ensuredStripeCustomer, orderPaymentIntent.payment_method)
            .then(response => {
              if (response.errors) {
                return { ...sendMessageResponse, paymentMethodSaved: false };
              }
              return { ...sendMessageResponse, paymentMethodSaved: true };
            })
            .catch(() => {
              return { ...sendMessageResponse, paymentMethodSaved: false };
            });
        }

        return Promise.resolve({ ...sendMessageResponse, paymentMethodSaved: true });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);

        if (createdOrderId) {
          // The order already exists: send the guest to it instead of the
          // checkout form. The initial message may not have been sent.
          return {
            orderId: createdOrderId,
            messageSuccess: false,
            paymentMethodSaved: true,
          };
        }

        // The order was not created. initiateOrderError is already in the
        // store, so the checkout page shows the error and the guest can retry.
        return undefined;
      }
    },
    [
      currentUser,
      stripeCustomerFetched,
      onInitiateOrder,
      onConfirmPayment,
      onSendMessage,
      onSavePaymentMethod,
      history,
      ltPlusSubscriptionIdRef,
    ]
  );

  const handleApplyPromoCode = useCallback(
    async promoCode => {
      const tripInsurance = getTripInsuranceSelection(speculatedTransaction);

      await getSpeculateTransaction(
        {
          ...pageData,
          tripInsurance,
          promoCode,
        },
        true
      );
    },
    [pageData, speculatedTransaction, getSpeculateTransaction]
  );

  const handleApplyInsurance = useCallback(
    async tripInsurance => {
      if (shouldSkipInsuranceSpeculationDuringLtPlus(isCompletingLtPlusSubscriptionRef.current)) {
        return;
      }

      const promoCode = get(speculatedTransaction, 'attributes.protectedData.promoCode.code', null);
      const updatedPageData = { ...pageData, tripInsurance };

      persistCheckoutPageData(updatedPageData);
      setPageData(updatedPageData);

      await getSpeculateTransaction(
        {
          ...updatedPageData,
          promoCode,
        },
        true
      );
    },
    [pageData, speculatedTransaction, getSpeculateTransaction, isCompletingLtPlusSubscriptionRef]
  );

  const handleApplyGuestLiabilityProtection = useCallback(
    async guestLiabilityProtection => {
      if (
        shouldSkipGlpFieldChangeSpeculation({
          isCompletingLtPlusSubscription: isCompletingLtPlusSubscriptionRef.current,
          ltPlusSubscriptionId: ltPlusSubscriptionIdRef.current,
          currentUser,
        })
      ) {
        return;
      }

      const tripInsurance = getTripInsuranceSelection(speculatedTransaction);
      const promoCode = get(speculatedTransaction, 'attributes.protectedData.promoCode.code', null);
      const updatedPageData = { ...pageData, guestLiabilityProtection };

      persistCheckoutPageData(updatedPageData);
      setPageData(updatedPageData);

      if (shouldSkipLtPlusSelectionSpeculation(currentUser, guestLiabilityProtection)) {
        return;
      }

      await getSpeculateTransaction(
        {
          ...updatedPageData,
          tripInsurance,
          promoCode,
        },
        true
      );
    },
    [
      pageData,
      speculatedTransaction,
      currentUser,
      getSpeculateTransaction,
      isCompletingLtPlusSubscriptionRef,
      ltPlusSubscriptionIdRef,
    ]
  );

  const handleCompleteLtPlusSubscription = useCallback(
    async ltPlusSubscriptionId => {
      ltPlusSubscriptionIdRef.current = ltPlusSubscriptionId;

      const tripInsurance = getTripInsuranceSelection(speculatedTransaction);
      const promoCode = get(speculatedTransaction, 'attributes.protectedData.promoCode.code', null);
      const updatedPageData = buildLtPlusCompletionPageData(pageData);

      persistCheckoutPageData(updatedPageData);

      const tx = await getSpeculateTransaction(
        buildLtPlusCompletionSpeculateParams({
          pageData,
          ltPlusSubscriptionId: ltPlusSubscriptionIdRef.current,
          currentUser,
          tripInsurance,
          promoCode,
        }),
        true
      );

      const refreshedUser = await ensureMembershipForLtPlusCheckout(fetchUser);

      const clientSecret = get(
        tx,
        'attributes.protectedData.stripePaymentIntents.default.stripePaymentIntentClientSecret',
        null
      );

      if (!clientSecret) {
        throw new Error('Unable to refresh booking payment details after LandTrust+ signup.');
      }

      postLtPlusSpeculatedTransactionRef.current = tx;
      setPageData(updatedPageData);

      return { clientSecret, user: refreshedUser };
    },
    [
      pageData,
      speculatedTransaction,
      currentUser,
      fetchUser,
      getSpeculateTransaction,
      ltPlusSubscriptionIdRef,
      postLtPlusSpeculatedTransactionRef,
    ]
  );

  const handleSubmit = useCallback(
    async values => {
      try {
        const {
          paymentIntent: submitPaymentIntent,
          message,
          saveAfterOnetimePayment,
          paymentMethod,
        } = values;

        const fetchedCurrentUser = await fetchUser();

        if (!isVerified(fetchedCurrentUser)) {
          setSubmitting(true);
          return;
        }

        setSubmitting(true);

        const bookingFees = get(pageData, 'bookingData', null);
        const storedTx = ensureTransaction(pageData.transaction);
        const tx = resolveCheckoutTransaction({
          postLtPlusSpeculatedTransaction: postLtPlusSpeculatedTransactionRef.current,
          speculatedTransaction,
          storedTransaction: storedTx,
        });
        postLtPlusSpeculatedTransactionRef.current = null;

        const startString = tx.booking.attributes.start;

        const startM = moment(startString)
          .utc()
          .format('LL');
        const bookingDisplayStart = new Date(startM);

        const endString = tx.booking.attributes.end;
        const endM = moment(endString)
          .utc()
          .format('LL');
        const bookingDisplayEnd = new Date(endM);

        const requestPaymentParams = {
          pageData,
          speculatedTransaction: tx,
          paymentIntent: submitPaymentIntent,
          bookingDisplayStart,
          bookingDisplayEnd,
          bookingFees,
          message,
          selectedPaymentMethod: paymentMethod,
          saveAfterOnetimePayment: !!saveAfterOnetimePayment,
          currentUser: fetchedCurrentUser,
        };

        const res = await handlePaymentIntent(requestPaymentParams);

        if (!res || !res.orderId) {
          return;
        }

        const { orderId, messageSuccess, paymentMethodSaved } = res;

        const routes = routeConfiguration();
        const initialMessageFailedToTransaction = messageSuccess ? null : orderId;
        const orderDetailsPath = pathByRouteName('InboxV2Page', routes, {
          tab: 'orders',
          id: orderId.uuid,
        });
        const initialValues = {
          initialMessageFailedToTransaction,
          savePaymentMethodFailed: !paymentMethodSaved,
        };

        initializeOrderPage(initialValues, routes, dispatch);
        clearData(STORAGE_KEY);
        history.push({
          pathname: orderDetailsPath,
          state: { showBookingModal: true },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('error', error);
        log.error(error, 'stripe-checkout-page-form-submit-failed');
      } finally {
        handleEndLtPlusSignup();
        setSubmitting(false);
      }
    },
    [
      fetchUser,
      pageData,
      speculatedTransaction,
      handlePaymentIntent,
      dispatch,
      history,
      handleEndLtPlusSignup,
      postLtPlusSpeculatedTransactionRef,
    ]
  );

  const onStripeInitialized = useCallback(
    stripe => {
      stripeRef.current = stripe;
      const tx = pageData ? pageData.transaction : null;

      const shouldFetchPaymentIntent =
        stripeRef.current &&
        !paymentIntent &&
        tx &&
        tx.id &&
        tx.booking &&
        tx.booking.id &&
        txIsPaymentPending(tx) &&
        !checkIsPaymentExpired(tx);

      if (shouldFetchPaymentIntent) {
        const { stripePaymentIntentClientSecret } =
          tx.attributes.protectedData && tx.attributes.protectedData.stripePaymentIntents
            ? tx.attributes.protectedData.stripePaymentIntents.default
            : {};

        onRetrievePaymentIntent({ stripe, stripePaymentIntentClientSecret });
      }
    },
    [pageData, paymentIntent, onRetrievePaymentIntent]
  );

  return {
    pageData,
    dataLoaded,
    submitting,
    usedPromoCode,
    setSubmitting,
    handleSubmit,
    handleApplyPromoCode,
    handleApplyInsurance,
    handleApplyGuestLiabilityProtection,
    handleCompleteLtPlusSubscription,
    handleBeginLtPlusSignup,
    handleEndLtPlusSignup,
    onStripeInitialized,
  };
};
